import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiCamera, FiTrash2, FiUpload, FiUsers } from 'react-icons/fi';
import { ITeam } from '../../pages/teams';
import api from '../../libs/axios';
import toast from 'react-hot-toast';
import ModalSelect from '../../ui/select-modal';

interface TeamOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    team?: ITeam | null;
    mode: 'create' | 'edit';
}

interface FormData {
    name: string;
    location: string;
    team_initials: string;
    logo: File | null;
    is_active: boolean;
    owner_id?: any;
}

const TeamOffcanvas: React.FC<TeamOffcanvasProps> = ({
    isOpen,
    onClose,
    onSuccess,
    team,
    mode
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);

    const [formData, setFormData] = useState<FormData>({
        name: '',
        location: '',
        team_initials: '',
        owner_id: undefined,
        logo: null,
        is_active: true
    });

    const [errors, setErrors] = useState<Partial<FormData>>({});

    // Initialize form data when editing
    useEffect(() => {
        if (mode === 'edit' && team) {
            setFormData({
                name: team.name || '',
                location: team.location || '',
                team_initials: team.team_initials || '',
                logo: null,
                is_active: team.is_active,
                owner_id: team.owner_id
            });
            setLogoPreview(team.logo_url);
            setUploadedLogoUrl(team.logo_url || null);
        } else if (mode === 'create') {
            setFormData({
                name: '',
                location: '',
                team_initials: '',
                logo: null,
                is_active: true
            });
            setLogoPreview(null);
            setUploadedLogoUrl(null);
        }
    }, [mode, team, isOpen]);


    const fetchOwners = async (page: number, search?: string, limit: number = 10, ownerId?: number) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search ? { search } : {}),
                ...(ownerId ? { organizer_id: ownerId.toString() } : {}),
            });

            const response = await api.get(`/users/organizers?${params}`);

            const ownersData = response.data?.data?.data || response.data?.data || [];
            const paginationData = response.data?.data?.pagination || response.data?.pagination;

            const data = ownersData.map((org: any) => {
                const firstName = org.details?.first_name || '';
                const lastName = org.details?.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();

                return {
                    value: org.id.toString(),
                    label: fullName || org.phone_number || `User ${org.id}`,
                    description: org.phone_number,
                    data: org,
                };
            });

            const pagination = {
                current_page: paginationData?.current_page || page,
                per_page: paginationData?.per_page || limit,
                total: paginationData?.total || 0,
                total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
                has_next_page: paginationData?.has_next_page || false,
                has_prev_page: paginationData?.has_prev_page || page > 1,
                next_page: paginationData?.next_page || null,
                prev_page: paginationData?.prev_page || null,
            };

            return { data, pagination };
        } catch (error: any) {
            console.error("Error fetching owners:", error);
            throw error;
        }
    };

    // Handle input changes
    const handleInputChange = (field: keyof FormData, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    // Handle logo upload
    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                return;
            }

            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Image size should be less than 2MB');
                return;
            }

            setFormData(prev => ({
                ...prev,
                logo: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload logo file to server
    const uploadLogoFile = async (file: File): Promise<string> => {
        const uploadFormData = new FormData();
        uploadFormData.append('type', 'team_logos');
        uploadFormData.append('files', file);

        try {
            const response = await api.post('/media/upload', uploadFormData);
            const uploadedFiles = response.data?.files || [];

            if (!uploadedFiles.length) {
                throw new Error('Failed to upload logo file');
            }

            return uploadedFiles[0].url;
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || 'Failed to upload logo file');
        }
    };

    // Remove logo
    const handleRemoveLogo = () => {
        setFormData(prev => ({
            ...prev,
            logo: null
        }));
        setLogoPreview(mode === 'edit' && team?.logo_url ? team.logo_url : null);
        setUploadedLogoUrl(mode === 'edit' && team?.logo_url ? team.logo_url : null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: Partial<FormData> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Team name is required';
        } else if (formData.name.trim().length > 100) {
            newErrors.name = 'Team name must not exceed 100 characters';
        }

        if (!formData.location.trim()) {
            newErrors.location = 'Location is required';
        } else if (formData.location.trim().length > 100) {
            newErrors.location = 'Location must not exceed 100 characters';
        }

        if (!formData.team_initials.trim()) {
            newErrors.team_initials = 'Team initials are required';
        } else if (formData.team_initials.trim().length > 5) {
            newErrors.team_initials = 'Team initials must not exceed 5 characters';
        }

        if (!formData.owner_id) {
            newErrors.owner_id = 'Owner is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            let logoUrl = uploadedLogoUrl;

            // Upload new logo file if selected
            if (formData.logo) {
                toast.loading('Uploading logo...');
                logoUrl = await uploadLogoFile(formData.logo);
                toast.dismiss();
            }

            // Prepare the JSON payload for team creation/update
            const jsonPayload: any = {
                name: formData.name.trim(),
                location: formData.location.trim(),
                team_initials: formData.team_initials.trim(),
                is_active: formData.is_active,
                add_me: false
            };

            // Only include logo_url if it exists
            if (logoUrl) {
                jsonPayload.logo_url = logoUrl;
            }

            // Add owner_id if it exists
            if (formData.owner_id) {
                jsonPayload.owner_id = formData.owner_id;
            }

            // Remove undefined values
            Object.keys(jsonPayload).forEach(key => {
                if (jsonPayload[key] === undefined) {
                    delete jsonPayload[key];
                }
            });

            if (mode === 'create') {
                await api.post('/teams', jsonPayload, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                toast.success('Team created successfully!');
            } else {
                await api.put(`/teams/${team?.id}`, jsonPayload, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                toast.success('Team updated successfully!');
            }

            onSuccess();
            handleClose();
        } catch (error: any) {
            toast.dismiss();
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Handle close
    const handleClose = () => {
        if (!loading) {
            setFormData({
                name: '',
                location: '',
                team_initials: '',
                logo: null,
                is_active: true
            });
            setLogoPreview(null);
            setUploadedLogoUrl(null);
            setErrors({});
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-[70%] bg-white shadow-xl z-50 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b bg-primary-50">
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {mode === 'create' ? 'Create Team' : 'Edit Team'}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Please describe your Team
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            disabled={loading}
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <form onSubmit={handleSubmit}>
                            {/* Logo Upload */}
                            <div className="flex flex-col items-center mb-8">
                                <div className="relative mb-4">
                                    {logoPreview ? (
                                        <div className="relative">
                                            <img
                                                src={logoPreview}
                                                alt="Team logo"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-primary-100"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleRemoveLogo}
                                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-24 h-24 rounded-full bg-primary-100 border-4 border-primary-200 flex items-center justify-center">
                                            <FiUsers className="w-8 h-8 text-primary-400" />
                                        </div>
                                    )}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    className="hidden"
                                />

                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    {logoPreview ? 'Change Logo' : 'Upload Team Logo'}
                                </button>
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    PNG, JPG up to 2MB
                                </p>
                            </div>

                            {/* Team Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Team Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Enter team name"
                                    disabled={loading}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.name ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                                )}
                            </div>

                            {/* Location */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location *
                                </label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={(e) => handleInputChange('location', e.target.value)}
                                    placeholder="Enter location"
                                    disabled={loading}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.location ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                />
                                {errors.location && (
                                    <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                                )}
                            </div>

                            {/* Team Initials */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Team Initials * (e.g. IND, AUS, PBKS)
                                </label>
                                <input
                                    type="text"
                                    value={formData.team_initials}
                                    onChange={(e) => handleInputChange('team_initials', e.target.value.toUpperCase())}
                                    placeholder="Enter initials"
                                    maxLength={5}
                                    disabled={loading}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.team_initials ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                />
                                {errors.team_initials && (
                                    <p className="text-red-500 text-sm mt-1">{errors.team_initials}</p>
                                )}
                            </div>

                            {/* Owner Selection Field */}
                            <ModalSelect
                                label="Select Owner"
                                placeholder="Choose an owner"
                                value={formData.owner_id ? formData.owner_id.toString() : ''}
                                onChange={(option) => {
                                    setFormData((prev) => ({ ...prev, owner_id: Number(option.value) }));
                                }}
                                fetchOptions={(page, search, limit) =>
                                    fetchOwners(page, search, limit, formData?.owner_id)
                                }
                                required={true}
                                error={errors.owner_id}
                                searchPlaceholder="Search by name or phone..."
                                renderOption={(option) => (
                                    <div>
                                        <div className="font-medium text-gray-900">{option.label}</div>
                                        <div className="text-sm text-gray-500 flex items-center mt-0.5">
                                            {option.description}
                                        </div>
                                    </div>
                                )}
                            />

                            {/* Status */}
                            <div className="my-4">
                                <div className="flex items-center mb-2">
                                    <label className="block text-sm font-medium text-gray-700 mr-2">
                                        Status
                                    </label>

                                    {/* Tooltip Icon */}
                                    <div className="relative group">
                                        <div className="w-4 h-4 flex items-center justify-center text-gray-500 border border-gray-400 rounded-full text-xs cursor-pointer hover:bg-gray-100">
                                            i
                                        </div>
                                        {/* Tooltip text */}
                                        <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-md px-2 py-1 w-56 z-10 shadow-md">
                                            Toggle the team's status. Active teams are visible to players, while inactive teams are hidden.
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        id="active-status"
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                        disabled={loading}
                                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                                    />
                                    <label htmlFor="active-status" className="ml-2 text-sm text-gray-700">
                                        Active Team
                                    </label>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="border-t bg-gray-50 p-6">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        {mode === 'create' ? 'Creating...' : 'Updating...'}
                                    </div>
                                ) : (
                                    mode === 'create' ? 'Create Team' : 'Update Team'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TeamOffcanvas;